"use client";
import { cn } from "@/lib/utils";
import React, { ComponentPropsWithoutRef } from 'react'; // Combined React import

interface MarqueeProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * Optional CSS class name to apply custom styles
   */
  className?: string;
  /**
   * Whether to reverse the animation direction
   * @default false
   */
  reverse?: boolean;
  /**
   * Whether to pause the animation on hover
   * @default false
   */
  pauseOnHover?: boolean;
  /**
   * Content to be displayed in the marquee
   */
  children: React.ReactNode;
  /**
   * Whether to animate vertically instead of horizontally
   * @default false
   */
  vertical?: boolean;
  /**
   * Number of times to repeat the content
   * @default 4
   */
  repeat?: number;
  /**
   * The gap between repeated child elements.
   * @default "1rem"
   */
  gap?: string;
  /**
   * The duration of one scroll cycle.
   * @default "40s"
   */
  duration?: string;
}

export function Marquee({
  className,
  reverse = false,
  pauseOnHover = false,
  children,
  vertical = false,
  repeat = 4,
  gap = "1rem", // Default gap
  duration = "40s", // Default duration
  ...props
}: MarqueeProps) {
  return (
    <div
      {...props}
      style={{
        '--duration': duration,
        '--gap': gap,
      } as React.CSSProperties} // Set CSS custom properties
      className={cn(
        "group flex overflow-hidden p-2 [gap:var(--gap)]", // Removed hardcoded [--duration:40s], [gap:var(--gap)] will use the CSS variable
        {
          "flex-row": !vertical,
          "flex-col": vertical,
        },
        className,
      )}
    >
      {Array(repeat)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className={cn("flex shrink-0 justify-around [gap:var(--gap)]", { // This inner div also uses [gap:var(--gap)]
              "animate-marquee flex-row": !vertical,
              "animate-marquee-vertical flex-col": vertical,
              "hover:[animation-play-state:paused]": pauseOnHover,
              "[animation-direction:reverse]": reverse,
            })}
          >
            {children}
          </div>
        ))}
    </div>
  );
}
